// ===== POC Cuidados Paliativos (PNCP) - Produção SUS =====
// Substitui o antigo mocks/susProduction.js. Catálogo e regras de tela ficam
// aqui; itens, pendências, exportações e atividades educativas vêm da API.

import moment from "moment";
import { apiDelete, apiGet, apiPost, query } from "./http";

export const PNCP_MINIMUM_PER_MONTH = 40;

export const ITEM_STATUS = {
  pending: "a_confirmar",
  issue: "pendencia",
  confirmed: "confirmado",
  discarded: "descartado",
  exported: "exportado"
};

export const CBO_LABELS = {
  "225125": "Médico clínico",
  "223505": "Enfermeiro",
  "251510": "Psicólogo clínico",
  "251605": "Assistente social",
  "322205": "Técnico de enfermagem"
};

const CP_TEAM_CBOS = ["225125", "223505", "251510", "251605"];

// Códigos da Portaria SAES/MS nº 4.288/2026 (revogou a 2.085/2024 e excluiu o 03.11.02.001-1).
// CBOs aceitos e exigência de CID são provisórios: conferir no SIGTAP da competência.
export const PROCEDURES = {
  "0311010016": {
    name: "Atendimento em CP na atenção especializada",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: true
  },
  "0311010040": {
    name: "Interconsulta em CP (solicitante)",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311010059": {
    name: "Interconsulta em CP (executante)",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311010067": {
    name: "Atendimento ao processo de luto",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311010070": {
    name: "Consultoria em CP para profissionais de saúde (executante)",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311010091": {
    name: "Registro de planejamento antecipado de cuidados",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311010105": {
    name: "Registro de diretivas antecipadas de vontade",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311020062": {
    name: "Consultoria em CP para profissionais de saúde (solicitante)",
    instrument: "BPA-I",
    cbos: CP_TEAM_CBOS,
    requiresCid: false
  },
  "0311020020": {
    name: "Atividade educativa em CP com profissionais da RAS",
    instrument: "BPA-C",
    cbos: CP_TEAM_CBOS
  },
  "0311020038": {
    name: "Atividade educativa em CP com grupo de familiares",
    instrument: "BPA-C",
    cbos: CP_TEAM_CBOS
  },
  "0311020046": {
    name: "Atividade educativa em CP com a comunidade",
    instrument: "BPA-C",
    cbos: CP_TEAM_CBOS
  }
};

// Atividades educativas (BPA consolidado): a equipe registra cada uma na tela de Produção SUS.
export const EDUCATIONAL_PROCEDURES = ["0311020020", "0311020038", "0311020046"];

// Grupo de familiares: a portaria recomenda pelo menos 10 participantes.
export const FAMILY_GROUP_CODE = "0311020038";
export const FAMILY_GROUP_MIN_PARTICIPANTS = 10;

// Pedido de apoio respondido: consultoria (sem o paciente) ou interconsulta (com o paciente).
export function matrixExecutantCode(modality) {
  return modality === "teleinterconsulta" ? "0311010059" : "0311010070";
}

export function formatProcedureCode(code) {
  return `${code.slice(0, 2)}.${code.slice(2, 4)}.${code.slice(4, 6)}.${code.slice(6, 9)}-${code.slice(9)}`;
}

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

/** { code: "YYYYMM", label: "Mês/AAAA", daysToClose } da competência informada (padrão: a atual). */
export function competenceInfo(code) {
  const today = moment().startOf("day");
  const month = code ? moment(code, "YYYYMM") : today.clone();
  return {
    code: month.format("YYYYMM"),
    label: `${MONTH_NAMES[month.month()]}/${month.year()}`,
    daysToClose: Math.max(0, month.clone().endOf("month").diff(today, "days"))
  };
}

export function currentCompetence() {
  return competenceInfo();
}

// Sigla para o cabeçalho do BPA: iniciais das palavras do nome da equipe.
function acronymOf(name) {
  return (name || "")
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 6) || "LILA";
}

function toTeam(t) {
  const team = t || {};
  return {
    id: team.Id,
    teamType: team.TeamType,
    name: team.Name || "",
    teamLabel: team.TeamType === "matricial" ? "Equipe matricial" : "Equipe assistencial",
    municipality: team.Municipality || "",
    cnes: team.Cnes || "",
    ine: team.Ine || "",
    ibge: team.Ibge || "",
    cnpj: team.Cnpj || "",
    acronym: acronymOf(team.Name),
    destination: team.Municipality ? `SMS ${team.Municipality}` : "SECRETARIA DE SAUDE"
  };
}

const STATUS_BY_CODE = {
  0: ITEM_STATUS.pending,
  1: ITEM_STATUS.confirmed,
  2: ITEM_STATUS.discarded,
  3: ITEM_STATUS.exported
};

function sexOf(gender) {
  const value = String(gender || "").trim().toUpperCase();
  if (value.startsWith("F")) return "F";
  if (value.startsWith("M")) return "M";
  return null;
}

// Item da API → linha da tela. Item a confirmar ou confirmado com pendência aparece como "Pendência".
function toRow(item) {
  const issues = item.Issues || [];
  let status = STATUS_BY_CODE[item.Status] || ITEM_STATUS.pending;
  if (issues.length > 0 && (status === ITEM_STATUS.pending || status === ITEM_STATUS.confirmed)) {
    status = ITEM_STATUS.issue;
  }
  const manual = item.SourceType === "atividade_educativa";
  const patient = item.Patient;
  const professional = item.Professional || {};
  return {
    id: item.Id,
    date: item.ServiceDate,
    code: item.ProcedureCode,
    procedureName: item.ProcedureName,
    instrument: item.Instrument,
    patientId: patient ? patient.Id : null,
    patient: patient
      ? {
        name: patient.Name,
        cns: patient.Cns || "",
        cid: patient.Cid || "",
        birthDate: patient.BirthDate,
        sex: sexOf(patient.Gender)
      }
      : null,
    quantity: item.Quantity || 1,
    professional: {
      name: professional.Name || "",
      cbo: professional.Cbo || "",
      cns: professional.Cns || ""
    },
    origin: item.Origin,
    status,
    issues,
    exportFile: item.ExportFileName || null,
    manual,
    activityId: manual ? item.SourceId : null,
    live: false,
    loading: false
  };
}

/** Itens da competência (a API gera as sugestões que faltam), exportações e meta. */
export async function getSusProduction(competence) {
  const data = await apiGet(`/sus-production${query({ competence })}`);
  return {
    team: toTeam(data.Team),
    competence: competenceInfo(data.Competence),
    goal: data.Goal,
    goalTotal: data.GoalTotal,
    rows: (data.Items || []).map(toRow),
    exports: (data.Exports || []).map((e) => ({
      id: e.Id,
      fileName: e.FileName,
      at: e.CreatedAt,
      by: e.ProfessionalName || "",
      itemCount: e.ItemCount
    }))
  };
}

export async function confirmItems(ids) {
  return apiPost("/sus-production/items/confirm", { Ids: ids });
}

export async function discardItems(ids) {
  return apiPost("/sus-production/items/discard", { Ids: ids });
}

export async function undoItems(ids) {
  return apiPost("/sus-production/items/undo", { Ids: ids });
}

/** Grava o arquivo gerado na tela e marca os itens confirmados como exportados. */
export async function registerExport({ competence, fileName, content, itemIds }) {
  return apiPost("/sus-production/exports", {
    Competence: competence,
    FileName: fileName,
    Content: content,
    ItemIds: itemIds
  });
}

/** Atividade educativa (BPA-C). O item entra já confirmado. */
export async function addEducationalActivity(activity) {
  return apiPost("/sus-production/activities", {
    Code: activity.code,
    Date: activity.date,
    Description: activity.description,
    Participants: Number(activity.participants),
    ProfessionalName: activity.professionalName,
    Cbo: activity.cbo
  });
}

export async function removeEducationalActivity(activityId) {
  return apiDelete(`/sus-production/activities/${activityId}`);
}
